import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import EditProfileModal from './EditProfileModal';
import AddSkillModal from './AddSkillModal';
import KycVerificationWidget from './KycVerificationWidget';
import { fetchCandidateSkills, getCandidateByMobile, fetchAllActiveSkills, updateCandidateResume, downloadResume, getMobileNumber, fetchCandidateKYCDetails } from '../services/apiService';
import { getCandidateId } from '../services/authService';
import FullPageLoader from './FullPageLoader';
import { useLanguage } from '../contexts/LanguageContext';

const Profile = () => {
    const location = useLocation();
    const { isAuthenticated, currentUser, signOut, loading: authLoading } = useAuth();
    const { showToast } = useToast();
    const [activeSection, setActiveSection] = useState('personal');
    const [showKycSection, setShowKycSection] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSkillModalOpen, setIsSkillModalOpen] = useState(false);
    const [userSkills, setUserSkills] = useState([]);
    const [profileData, setProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [skillsLoading, setSkillsLoading] = useState(false);
    const [allActiveSkills, setAllActiveSkills] = useState([]);
    const [resumeUploading, setResumeUploading] = useState(false);
    const [resumeDownloading, setResumeDownloading] = useState(false);
    const [resumeFileName, setResumeFileName] = useState('');
    const [resumeFilePath, setResumeFilePath] = useState('');
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    // New: local preview for newly selected resume before upload completes
    const [selectedResumeFile, setSelectedResumeFile] = useState(null);
    const [selectedResumePreviewUrl, setSelectedResumePreviewUrl] = useState(null);
    const [candidateIdForResume, setCandidateIdForResume] = useState(null);
    // Language handling: use global LanguageContext, keep a local code for dropdown UI only
    const { availableLanguages: ctxLanguages } = useLanguage();

    // Programmatic token is initialized in App; no direct token fetch here
    const [kycStatus, setKycStatus] = useState({
        isAadhaarVerified: false,
        isPanVerified: false,
        nameOnPan: null,
        panNumber: null,
        nameOnAadhaar: null,
        aadhaarNumber: null
    });
    const [kycLoading, setKycLoading] = useState(true);
    // Removed unused availableLanguages state - using ctxLanguages from context
    const [languagesLoading, setLanguagesLoading] = useState(false);
    const [hasLoadedResumeDetails, setHasLoadedResumeDetails] = useState(false);
    const [isLoadingResumeDetails, setIsLoadingResumeDetails] = useState(false);
    const [hasLoadedSkills, setHasLoadedSkills] = useState(false);
    const [hasLoadedLanguages, setHasLoadedLanguages] = useState(false);
    

    // Removed local programmatic user initialization

    // Handle scroll to skills section when navigated from JobCard
    useEffect(() => {
        if (location.state?.scrollToSkills) {
            // Wait for component to render and skills section to be available
            const timer = setTimeout(() => {
                const skillsSection = document.getElementById('skills');
                if (skillsSection) {
                    skillsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    // Also set active section to skills for visual feedback
                    setActiveSection('skills');
                }
                // Clear the state to prevent re-scrolling on subsequent renders
                window.history.replaceState({}, document.title);
            }, 300);
            
            return () => clearTimeout(timer);
        }
    }, [location.state]);

    // Cleanup preview URL on unmount or when it changes
    useEffect(() => {
        return () => {
            if (selectedResumePreviewUrl) {
                try { URL.revokeObjectURL(selectedResumePreviewUrl); } catch (_) {}
            }
        };
    }, [selectedResumePreviewUrl]);

    // Handle window resize for responsive design
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load all candidate data from single API call (profile + resume)
    const loadCandidateData = useCallback(async () => {
        // Prevent duplicate calls if already loading
        if (profileLoading || hasLoadedResumeDetails || isLoadingResumeDetails) {
            console.log('[Profile] ⚠️ Candidate data already loading or loaded, skipping duplicate call');
            return;
        }

        try {
            setProfileLoading(true);
            setIsLoadingResumeDetails(true);
            setHasLoadedResumeDetails(true);
            console.log('[Profile] 🔄 Calling getDetailsWithMob API...');
            
            const result = await getCandidateByMobile();
            console.log('[Profile] Consolidated API Response:', result);
            
            if (result.success && result.data) {
                console.log('[Profile] ✅ Candidate data loaded successfully:', result.data);
                
                // Set profile data
                setProfileData(result.data);
                
                // Extract resume data from same response
                const filePath = result.data.filePath;
                const candidateId = result.data.candidateId || result.data.id;
                
                console.log('[Profile] Resume filePath from API:', filePath);
                console.log('[Profile] Candidate ID from API:', candidateId);
                
                // Extract filename from filePath if it exists
                let resumeFileName = null;
                if (filePath && filePath.trim() !== '' && filePath !== 'null' && filePath !== null) {
                    // Extract filename from path (e.g., "path/to/resume.pdf" -> "resume.pdf")
                    resumeFileName = filePath.split('/').pop() || filePath;
                    console.log('[Profile] ✅ Resume file found:', resumeFileName);
                    
                    // Store the full filePath and candidateId for download
                    setResumeFilePath(filePath);
                    setCandidateIdForResume(candidateId);
                } else {
                    console.log('[Profile] ❌ No valid filePath found');
                    setResumeFilePath(null);
                    setCandidateIdForResume(null);
                }
                
                setResumeFileName(resumeFileName);
                console.log('[Profile] Final resume filename state:', resumeFileName);
                
            } else if (result.success && result.status === 404) {
                console.log('[Profile] ⚠️ No candidate data found (404) - User may need to complete profile');
                setProfileData(null);
                setResumeFileName(null);
            } else {
                console.error('[Profile] ❌ Failed to load candidate data:', result.error);
                console.error('[Profile] Full error response:', result);
                setProfileData(null);
                setResumeFileName(null);
            }
        } catch (error) {
            console.error('[Profile] 💥 Exception while loading candidate data:', error);
            setProfileData(null);
            setResumeFileName(null);
        } finally {
            setProfileLoading(false);
            setIsLoadingResumeDetails(false);
            console.log('[Profile] Candidate data loading completed.');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    
    // Load available languages from LanguageContext (no API calls here)
    const loadAvailableLanguages = useCallback(async () => {
        if (languagesLoading || hasLoadedLanguages) {
            return;
        }
        try {
            setLanguagesLoading(true);
            console.log('[Profile] Loading available languages from LanguageContext...');
            // Languages are available from ctxLanguages - no state update needed
            setHasLoadedLanguages(true);
        } catch (error) {
            console.error('[Profile] Error accessing LanguageContext languages:', error);
            setHasLoadedLanguages(true);
        } finally {
            setLanguagesLoading(false);
        }
    }, [ctxLanguages, languagesLoading, hasLoadedLanguages]); // eslint-disable-line react-hooks/exhaustive-deps

    // Language change functionality removed as it's not currently used
    
    // Load candidate skills from API (with duplicate call prevention)
    const loadCandidateSkills = useCallback(async () => {
        // Prevent duplicate calls if already loading or already loaded
        if (skillsLoading || hasLoadedSkills) {
            console.log('[Profile] ⚠️ Skills already loading or loaded, skipping duplicate call');
            return;
        }

        try {
            setSkillsLoading(true);
            setHasLoadedSkills(true); // Mark as loaded immediately to prevent race conditions
            console.log('[Profile] 🔄 Loading candidate skills...');
            
            // First, get all available skills for lookup (cache-aware to avoid extra API calls)
            let skillsLookup = {};
            let allSkillsResult = { success: true, data: allActiveSkills };
            
            if (!allActiveSkills || allActiveSkills.length === 0) {
                console.log('[Profile] 🔄 Active skills cache empty → calling fetchAllActiveSkills API');
                allSkillsResult = await fetchAllActiveSkills();
                if (allSkillsResult.success && allSkillsResult.data) {
                    // Store all active skills for use in AddSkillModal
                    setAllActiveSkills(allSkillsResult.data);
                    console.log('[Profile] ✅ Active skills loaded and cached:', allSkillsResult.data.length, 'skills');
                }
            } else {
                console.log('[Profile] ✅ Using cached active skills, no fetchAllActiveSkills API call needed');
            }
            
            if (allSkillsResult.success && allSkillsResult.data) {
                console.log('[Profile] All skills for lookup:', allSkillsResult.data);
                
                // Create a lookup map from skill ID to skill name
                allSkillsResult.data.forEach(skill => {
                    if (typeof skill === 'object') {
                        // Handle multiple possible ID and name field combinations
                        const skillId = skill.id || skill.skillId || skill.skill_id;
                        const skillName = skill.name || skill.skillName || skill.skill_name || skill.title || skill.label;
                        
                        if (skillId && skillName) {
                            skillsLookup[skillId] = skillName;
                            // Also map string version of ID
                            skillsLookup[String(skillId)] = skillName;
                        }
                    }
                });
            }
            
            console.log('[Profile] 🔄 Calling fetchCandidateSkills API...');
            const result = await fetchCandidateSkills();
            
            if (result.success && result.data) {
                console.log('[Profile] ✅ Candidate skills loaded:', result.data);
                
                // Handle different API response formats
                let skillNames = [];
                
                if (Array.isArray(result.data)) {
                    skillNames = result.data.map(skill => {
                        // If skill is a string, check if it's an ID that needs lookup
                        if (typeof skill === 'string') {
                            // Try to find skill name in lookup first
                            if (skillsLookup[skill]) {
                                return skillsLookup[skill];
                            }
                            // If it's not a number, it might already be a skill name
                            if (isNaN(skill)) {
                                return skill;
                            }
                            // If it's a number but not in lookup, show as fallback
                            return `Skill ${skill}`;
                        }
                        // If skill is an object, try to get name from lookup first
                        if (typeof skill === 'object') {
                            // Try to get skill name from lookup using skillId
                            const skillId = skill.skillId || skill.id || skill.skill_id;
                            if (skillId && skillsLookup[skillId]) {
                                return skillsLookup[skillId];
                            }
                            // Fallback to direct properties
                            return skill.skillName || skill.name || skill.skill_name || 
                                   skill.title || skill.label || `Skill ${skillId || 'Unknown'}`;
                        }
                        return 'Unknown Skill';
                    });
                } else if (typeof result.data === 'object' && result.data.skills) {
                    // Handle case where skills are nested in a skills property
                    skillNames = result.data.skills.map(skill => {
                        if (typeof skill === 'string') {
                            return skill;
                        }
                        if (typeof skill === 'object') {
                            const skillId = skill.skillId || skill.id;
                            if (skillId && skillsLookup[skillId]) {
                                return skillsLookup[skillId];
                            }
                            return skill.skillName || skill.name || skill.skill || 
                                   skill.title || skill.label || `Skill ${skillId || 'Unknown'}`;
                        }
                        return 'Unknown Skill';
                    });
                }
                
                console.log('[Profile] ✅ Extracted skill names:', skillNames);
                setUserSkills(skillNames);
                console.log('[Profile] ✅ Skills processing completed');
        } else {
            console.log('[Profile] ❌ Failed to load candidate skills:', result.error);
            // Fallback to default skills if API fails
            setUserSkills(['Flutter', 'Dart', 'Firebase', 'REST APIs', 'Git', 'Agile']);
        }
    } catch (error) {
        console.error('[Profile] Error loading skills:', error);
        // Fallback to default skills on error
        setUserSkills(['Flutter', 'Dart', 'Firebase', 'REST APIs', 'Git', 'Agile', 'React', 'Node.js']);
    } finally {
        setSkillsLoading(false);
    }
}, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Smooth scroll with offset for fixed header
    const scrollToWithOffset = (elementId) => {
        const el = document.getElementById(elementId);
        if (!el) return;
        const header = document.querySelector('.top-nav');
        const headerHeight = header ? header.offsetHeight : 0;
        const offset = headerHeight + 16; // add small spacing below header
        const y = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    const handleNavClick = (section) => {
        setActiveSection(section);
        if (section === 'kyc') {
            setShowKycSection(true);
        }
        scrollToWithOffset(section);
    };

    // Load KYC data from fetchCandidateKYCDetails API
    const loadKYCData = useCallback(async () => {
        try {
            setKycLoading(true);
            const result = await fetchCandidateKYCDetails();
            
            if (result.success && result.data) {
                const newKycStatus = {
                    isAadhaarVerified: result.data.aadhaarVerified || false,
                    isPanVerified: result.data.panVerified || false,
                    nameOnPan: result.data.nameOnPan || null,
                    panNumber: result.data.panNumber || null,
                    nameOnAadhaar: result.data.nameOnAadhaar || null,
                    aadhaarNumber: result.data.aadhaarNumber || null
                };
                
                setKycStatus(newKycStatus);
            }
        } catch (error) {
            console.error('[Profile] Error loading KYC data:', error);
        } finally {
            setKycLoading(false);
        }
    }, []);

    // Load profile data and skills when component mounts - ONLY ONCE
    useEffect(() => {
        if (isAuthenticated) {
            console.log('[Profile] 🚀 Initial load triggered');
            loadCandidateData();
            loadCandidateSkills();
            loadAvailableLanguages(); // Load app languages on profile page load
            loadKYCData(); // Load KYC data from fetchCandidateKYCDetails API
        }
    }, [isAuthenticated, loadCandidateData, loadCandidateSkills, loadAvailableLanguages, loadKYCData]); // eslint-disable-line react-hooks/exhaustive-deps

    const openEditProfileModal = () => setIsEditModalOpen(true);
    const closeEditModal = () => setIsEditModalOpen(false);
    const openSkillsModal = () => setIsSkillModalOpen(true);
    const closeSkillsModal = () => setIsSkillModalOpen(false);
    
    const handleSaveSkills = async (updatedSkillsData) => {
        // If updated skills data is provided, process and update the display
        if (updatedSkillsData) {
            console.log('[Profile] Received updated skills data:', updatedSkillsData);
            
            // Create a lookup map from skill ID to skill name using allActiveSkills
            const skillsLookup = {};
            allActiveSkills.forEach(skill => {
                if (typeof skill === 'object') {
                    const skillId = skill.id || skill.skillId || skill.skill_id;
                    const skillName = skill.name || skill.skillName || skill.skill_name || skill.title || skill.label;
                    
                    if (skillId && skillName) {
                        skillsLookup[skillId] = skillName;
                        skillsLookup[String(skillId)] = skillName;
                    }
                }
            });
            
            // Process the skills data to extract skill names
            let skillNames = [];
            if (Array.isArray(updatedSkillsData)) {
                skillNames = updatedSkillsData.map(skill => {
                    if (typeof skill === 'string') {
                        // If it's a string, check if it's an ID that needs lookup
                        return skillsLookup[skill] || skill;
                    }
                    if (typeof skill === 'object') {
                        // Try to get skill name from lookup first using ID
                        const skillId = skill.skillId || skill.id;
                        if (skillId && skillsLookup[skillId]) {
                            return skillsLookup[skillId];
                        }
                        // Fallback to direct properties
                        return skill.skillName || skill.name || skill.skill || 
                               skill.title || skill.label || `Skill ${skillId || 'Unknown'}`;
                    }
                    return 'Unknown Skill';
                });
            }
            
            console.log('[Profile] Updating skills display with:', skillNames);
            setUserSkills(skillNames);
        }
        
        // Close the modal
        closeSkillsModal();
    };

    const handleResumeUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type and size
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!allowedTypes.includes(file.type)) {
            alert('Please upload a PDF, DOC, or DOCX file.');
            return;
        }

        if (file.size > maxSize) {
            alert('File size must be less than 5MB.');
            return;
        }
        // Set local preview so user can see filename/thumbnail while uploading
        try {
            setSelectedResumeFile(file);
            if (file.type === 'application/pdf') {
                const url = URL.createObjectURL(file);
                setSelectedResumePreviewUrl(url);
            } else {
                setSelectedResumePreviewUrl(null);
            }
        } catch (e) {
            console.warn('[Profile] Could not prepare preview URL:', e);
        }

        console.log('[Profile] Starting resume upload:', file.name);
        setResumeUploading(true);

        try {
            // Get candidate ID for the upload
            const candidateId = getCandidateId();
            if (!candidateId) {
                throw new Error('Candidate ID not found. Please sign in again.');
            }

            console.log('[Profile] Uploading resume for candidate:', candidateId);

            // Upload the resume using the API
            const result = await updateCandidateResume(candidateId, file, file.name);

            if (result.success) {
                console.log('[Profile] Resume uploaded successfully, reloading candidate data...');
                loadCandidateData(); // Reload candidate data after upload
                console.log('[Profile] ✅ Resume uploaded successfully');
                
                // Show success message with toast
                showToast('Resume uploaded successfully!', 'success');
                
                // Resume data will be refreshed by loadCandidateData() call above
                console.log('[Profile] 🔄 Resume data refreshed via consolidated API call');
                
                // Clear the file input
                const fileInput = document.getElementById('resume-file');
                if (fileInput) {
                    fileInput.value = '';
                }
                // Clear local preview
                if (selectedResumePreviewUrl) {
                    try { URL.revokeObjectURL(selectedResumePreviewUrl); } catch (_) {}
                }
                setSelectedResumeFile(null);
                setSelectedResumePreviewUrl(null);
            }

        } catch (error) {
            console.error('[Profile] Error uploading resume:', error);
            alert(`Error uploading resume: ${error.message}`);
        } finally {
            setResumeUploading(false);
        }
    };

    // Handle resume download using proper API
    const handleDownloadResume = async () => {
        console.log('🔥 [Profile] DOWNLOAD BUTTON CLICKED - Starting resume download process');
        console.log('🔥 [Profile] This should call the downloadFileFromAWS API, NOT fetchCandidateKYCDetails');
        
        if (resumeFilePath && candidateIdForResume) {
            console.log('[Profile] Initiating resume download...');
            console.log('[Profile] Using candidateId:', candidateIdForResume);
            console.log('[Profile] Using filePath:', resumeFilePath);
            
            try {
                console.log('🔥 [Profile] About to call downloadResume API function...');
                setResumeDownloading(true);
                const result = await downloadResume(candidateIdForResume, resumeFilePath);
                if (result.success) {
                    console.log('[Profile] ✅ Resume download initiated successfully');
                    showToast(result.message || 'Resume download started', 'success');
                } else {
                    console.error('[Profile] ❌ Resume download failed:', result.error);
                    showToast(result.error || 'Failed to download resume', 'error');
                }
            } catch (error) {
                console.error('[Profile] ❌ Resume download error:', error);
                showToast('Error downloading resume. Please try again.', 'error');
            } finally {
                setResumeDownloading(false);
            }
        } else {
            console.error('[Profile] ❌ Missing resume data for download');
            console.log('[Profile] resumeFilePath:', resumeFilePath);
            console.log('[Profile] candidateIdForResume:', candidateIdForResume);
            showToast('Resume data not available. Please refresh the page.', 'error');
        }
    };

    // Remove resume (only clears local state, doesn't delete from server)
    // const removeResume = () => {
    //     setResumeFileName(null);
    //     setResumeFilePath(null);
    //     setCandidateIdForResume(null);
    // };

    // Handle KYC verification status changes
    const handleKycVerificationStatusChanged = useCallback((isAadhaarVerified, isPanVerified, kycData = {}) => {
        console.log('[Profile] 📥 Received KYC status update:', {
            isAadhaarVerified,
            isPanVerified,
            kycData
        });
        
        const newKycStatus = {
            isAadhaarVerified,
            isPanVerified,
            nameOnPan: kycData.nameOnPan || kycData.nameOnPAN || null,
            panNumber: kycData.panNumber || null,
            nameOnAadhaar: kycData.nameOnAadhaar || null,
            aadhaarNumber: kycData.aadhaarNumber || null
        };
        
        console.log('[Profile] 📝 Setting KYC status to:', newKycStatus);
        setKycStatus(newKycStatus);
        
        // Don't update profileData here to prevent infinite re-renders
        // KYC status is managed separately in kycStatus state
    }, []);

    // Handle sign out with confirmation
    const handleSignOut = () => {
        if (window.confirm('Are you sure you want to sign out?')) {
            signOut();
        }
    };

    // Scroll to section if navigated with hash (#kyc) or state { scrollTo: 'kyc' }
    useEffect(() => {
        const hashTarget = (location && typeof location.hash === 'string' && location.hash.startsWith('#'))
            ? location.hash.substring(1)
            : null;
        const stateTarget = location && location.state && location.state.scrollTo ? location.state.scrollTo : null;
        const target = hashTarget || stateTarget;
        if (target) {
            setActiveSection(target);
            // Defer to ensure DOM is painted
            setTimeout(() => {
                if (target === 'kyc') {
                    setShowKycSection(true);
                }
                // Actual scroll will also be triggered by showKycSection effect
                scrollToWithOffset(target);
            }, 0);
        } else {
            // No explicit target: default to personal and hide KYC section
            setShowKycSection(false);
            setActiveSection('personal');
        }
    }, [location]);

    // When KYC section is toggled visible, scroll to it with a reliable paint delay
    useEffect(() => {
        if (!showKycSection) return;
        // Double RAF to ensure layout is settled, plus a small timeout buffer
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setTimeout(() => scrollToWithOffset('kyc'), 50);
            });
        });
    }, [showKycSection]);


    // Calculate profile completion percentage with equal weights
    const calculateProfileCompletion = () => {
        const sections = [
            {
                name: 'Personal Information',
                completed: profileData && profileData.fullName && profileData.mobile && profileData.email,
                weight: 25 // 25% each (4 sections total)
            },
            {
                name: 'Resume Upload',
                completed: resumeFileName && resumeFileName.trim() !== '',
                weight: 25 // 25% each
            },
            {
                name: 'Skills Added',
                completed: userSkills && userSkills.length > 0,
                weight: 25 // 25% each
            },
            {
                name: 'KYC Verification',
                completed: kycStatus.isAadhaarVerified && kycStatus.isPanVerified,
                weight: 25 // 25% each
            }
        ];

        const completedWeight = sections.reduce((total, section) => {
            return total + (section.completed ? section.weight : 0);
        }, 0);

        return {
            percentage: completedWeight,
            sections: sections
        };
    };

    const profileCompletion = calculateProfileCompletion();

    const renderProfileCompletionCard = () => (
        <div className="profile-completion-card-wrapper" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #f3f4f6',
            height: 'fit-content'
        }}>
            <h3 style={{
                marginBottom: '0.75rem',
                color: '#1f2937',
                fontSize: '1rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
            }}>
                <i className="fas fa-chart-line" style={{color: '#2563eb'}}></i>
                Profile Completion
            </h3>

            <div style={{marginBottom: '0.75rem'}}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.75rem'
                }}>
                    <span style={{
                        fontSize: '0.875rem',
                        color: '#64748b',
                        fontWeight: 500
                    }}>Progress</span>
                    <span style={{
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: '#2563eb'
                    }}>{profileCompletion.percentage}%</span>
                </div>
                <div style={{
                    width: '100%',
                    background: '#e2e8f0',
                    borderRadius: '12px',
                    height: '12px',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        width: `${profileCompletion.percentage}%`,
                        background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                        borderRadius: '12px',
                        height: '12px',
                        transition: 'width 0.3s ease'
                    }}></div>
                </div>
            </div>

            <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
            }}>
                {profileCompletion.sections.map((section, index) => (
                    <li key={index} style={{
                        padding: '0.75rem 0',
                        borderBottom: index < profileCompletion.sections.length - 1 ? '1px solid #f1f5f9' : 'none',
                        fontSize: '0.875rem',
                        color: '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.75rem'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                        }}>
                            <i className={section.completed ? "fas fa-check-circle" : "fas fa-exclamation-circle"} style={{
                                color: section.completed ? '#10b981' : '#f59e0b',
                                fontSize: '1rem'
                            }}></i>
                            <span>{section.name}</span>
                        </div>
                        <span style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: section.completed ? '#10b981' : '#f59e0b',
                            minWidth: '30px',
                            textAlign: 'right'
                        }}>
                            {section.weight}%
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );

    // Get user data from profile API, currentUser, or localStorage
    const userData = currentUser || JSON.parse(localStorage.getItem('user') || '{}');
    
    // Helper function to clean up name display (avoid "null null null")
    const getCleanName = (fullName, fallbackName) => {
        if (fullName && fullName !== 'null' && !fullName.includes('null')) {
            return fullName;
        }
        if (fallbackName && fallbackName !== 'null' && !fallbackName.includes('null')) {
            return fallbackName;
        }
        return 'Loading...'; // Show loading instead of null values
    };
    
    const userName = getCleanName(profileData?.fullName, userData.name) || 'John Doe';
    const userMobile = profileData?.mobile || userData.mobile || '+91 98765 43210';
    
    // Debug logs removed to prevent console spam

    // While auth is initializing, show a neutral full-page loader
    if (authLoading) {
        return <FullPageLoader text="Loading..." />;
    }

    return (
        <div style={{ background: '#f8fafc' }}>
            <section style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #2563eb 100%)', color: 'white', padding: windowWidth <= 480 ? '1.5rem 0' : windowWidth <= 768 ? '2rem 16px' : '2.5rem 24px', position: 'relative', overflow: 'hidden', boxSizing: 'border-box' }}>
                <div style={{ position: 'relative', zIndex: 1, textAlign: 'left', margin: windowWidth <= 480 ? '0 16px' : '0', boxSizing: 'border-box', width: windowWidth <= 480 ? 'calc(100% - 32px)' : '100%', maxWidth: '100%' }}>
                    <h1 style={{ fontSize: windowWidth <= 480 ? '0.9rem' : windowWidth <= 768 ? '1.75rem' : '2.25rem', marginBottom: '0.5rem', fontWeight: 700, lineHeight: 1.1, whiteSpace: 'nowrap', width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                        <i className="fas fa-user" style={{ color: 'rgba(255,255,255,0.9)', fontSize: windowWidth <= 480 ? '0.8rem' : windowWidth <= 768 ? '1.5rem' : '2.25rem', marginRight: windowWidth <= 480 ? '8px' : '0.5rem' }}></i>My Profile
                    </h1>
                    <p style={{ fontSize: windowWidth <= 480 ? '0.8rem' : windowWidth <= 768 ? '0.875rem' : '1rem', opacity: 0.9, fontWeight: 400, lineHeight: '1.4' }}>Manage your personal information and career details</p>
                </div>
            </section>

            {/* Main Content */}
            <div style={{
                    display: 'flex',
                    width: '100%',
                    padding: window.innerWidth <= 480 ? '12px' : window.innerWidth <= 768 ? '16px' : '24px',
                    gap: window.innerWidth <= 768 ? '16px' : '24px',
                    flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
                }}>
                {/* Left Sidebar - Profile Navigation */}
                <aside className="profile-sidebar" style={{
                    width: window.innerWidth <= 768 ? '100%' : '280px',
                    flexShrink: 0
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: window.innerWidth <= 480 ? '8px' : '12px',
                        padding: window.innerWidth <= 480 ? '1rem' : '1.5rem',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        border: '1px solid #e5e7eb'
                    }}>
                        <h3 style={{
                            margin: '0 0 1.5rem 0',
                            color: '#1f2937',
                            fontSize: '1.125rem',
                            fontWeight: 600
                        }}>Profile Section</h3>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem'
                        }}>
                            {[
                                { id: 'personal', icon: 'fas fa-user', label: 'Personal Information' },
                                { id: 'resume', icon: 'fas fa-file-pdf', label: 'Resume' },
                                { id: 'skills', icon: 'fas fa-lightbulb', label: 'Skills' },
                                { id: 'kyc', icon: 'fas fa-id-card', label: 'KYC Verification' }
                            ].map(section => (
                                <a key={section.id}
                                   href={`#${section.id}`}
                                   style={{
                                       padding: '0.75rem',
                                       background: activeSection === section.id ? '#e0e7ff' : 'transparent',
                                       color: activeSection === section.id ? '#2563eb' : '#666',
                                       textDecoration: 'none',
                                       borderRadius: '0.375rem',
                                       fontWeight: 500,
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: '0.5rem',
                                       transition: 'all 0.2s'
                                   }}
                                   onClick={(e) => { e.preventDefault(); handleNavClick(section.id); }}>
                                    <i className={section.icon} style={{fontSize: '1.25rem', width: '20px', textAlign: 'center', marginRight: '0.75rem'}}></i>
                                    {section.label}
                                </a>
                            ))}
                            
                            {/* Sign Out Button */}
                            <button 
                                onClick={handleSignOut}
                                style={{
                                    padding: '0.75rem',
                                    background: 'transparent',
                                    color: '#dc2626',
                                    textDecoration: 'none',
                                    borderRadius: '0.375rem',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    transition: 'all 0.2s',
                                    border: 'none',
                                    cursor: 'pointer',
                                    width: '100%',
                                    textAlign: 'left',
                                    fontSize: 'inherit'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = '#fef2f2';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'transparent';
                                }}
                            >
                                <i className="fas fa-sign-out-alt" style={{fontSize: '1.25rem', width: '20px', textAlign: 'center', marginRight: '0.75rem'}}></i>
                                Sign Out
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Mobile Profile Completion Card */}
                {windowWidth <= 768 && (
                    <div className="profile-completion-mobile-wrapper">
                        {renderProfileCompletionCard()}
                    </div>
                )}

                {/* Profile Content */}
                <main className="profile-main-content" style={{flex: 1}}>
                    {/* Main Profile Card */}
                    <div style={{
                        background: 'white',
                        borderRadius: window.innerWidth <= 480 ? '8px' : '12px',
                        padding: window.innerWidth <= 480 ? '16px' : '24px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        border: '1px solid #e5e7eb'
                    }}>
                        {/* Personal Information Section */}
                        <div id="personal" style={{
                            borderBottom: '1px solid #e5e7eb',
                            paddingBottom: '24px',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '1.5rem'
                            }}>
                                <h3 style={{
                                    margin: '0',
                                    color: '#1f2937',
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem'
                                }}>
                                    <i className="fas fa-user" style={{color: '#2563eb', fontSize: '1.25rem', width: '20px', textAlign: 'center'}}></i>
                                    Personal Information
                                </h3>
                                <button 
                                    onClick={openEditProfileModal} 
                                    disabled={profileLoading || !profileData || kycLoading}
                                    style={{
                                        background: profileLoading || !profileData || kycLoading
                                            ? '#9ca3af' 
                                            : 'linear-gradient(135deg, #2563eb, #3b82f6)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '12px',
                                        cursor: profileLoading || !profileData || kycLoading ? 'not-allowed' : 'pointer',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        transition: 'all 0.3s ease',
                                        boxShadow: profileLoading || !profileData || kycLoading
                                            ? 'none' 
                                            : '0 2px 8px rgba(37, 99, 235, 0.3)',
                                        opacity: profileLoading || !profileData || kycLoading ? 0.6 : 1
                                    }}>
                                    {profileLoading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Loading Profile...
                                        </>
                                    ) : kycLoading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Loading KYC...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-edit"></i> Edit Profile
                                        </>
                                    )}
                                </button>
                            </div>
                            <div style={{
                                background: 'white',
                                borderRadius: '12px',
                                padding: '0.75rem 1.5rem 1.5rem 1.5rem',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                border: '1px solid #e5e7eb'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    margin: 0,
                                    padding: 0
                                }}>
                                    <div style={{flex: 1, margin: 0, padding: 0}}>
                                        {profileLoading ? (
                                            // Instagram-style skeleton loading for name
                                            <div style={{
                                                height: '2.25rem',
                                                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                                                backgroundSize: '200% 100%',
                                                animation: 'shimmer 1.5s infinite',
                                                borderRadius: '8px',
                                                marginBottom: '0.75rem',
                                                width: '60%'
                                            }}></div>
                                        ) : (
                                            <h2 style={{
                                                margin: '0',
                                                color: '#1f2937',
                                                fontSize: '1.75rem',
                                                fontWeight: 700,
                                                userSelect: 'none',
                                                WebkitUserSelect: 'none',
                                                MozUserSelect: 'none',
                                                msUserSelect: 'none'
                                            }}>{userName}</h2>
                                        )}
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5rem'
                                        }}>
                                            {profileLoading ? (
                                                // Instagram-style skeleton loading for contact info
                                                <>
                                                    {/* Email skeleton */}
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        margin: 0
                                                    }}>
                                                        <div style={{
                                                            width: '14px',
                                                            height: '14px',
                                                            background: '#e0e0e0',
                                                            borderRadius: '2px'
                                                        }}></div>
                                                        <div style={{
                                                            height: '14px',
                                                            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                                                            backgroundSize: '200% 100%',
                                                            animation: 'shimmer 1.5s infinite',
                                                            borderRadius: '4px',
                                                            width: '45%'
                                                        }}></div>
                                                    </div>
                                                    {/* Phone skeleton */}
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        margin: 0
                                                    }}>
                                                        <div style={{
                                                            width: '14px',
                                                            height: '14px',
                                                            background: '#e0e0e0',
                                                            borderRadius: '2px'
                                                        }}></div>
                                                        <div style={{
                                                            height: '14px',
                                                            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                                                            backgroundSize: '200% 100%',
                                                            animation: 'shimmer 1.5s infinite',
                                                            borderRadius: '4px',
                                                            width: '35%'
                                                        }}></div>
                                                    </div>
                                                    {/* Location skeleton */}
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        margin: 0
                                                    }}>
                                                        <div style={{
                                                            width: '14px',
                                                            height: '14px',
                                                            background: '#e0e0e0',
                                                            borderRadius: '2px'
                                                        }}></div>
                                                        <div style={{
                                                            height: '14px',
                                                            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                                                            backgroundSize: '200% 100%',
                                                            animation: 'shimmer 1.5s infinite',
                                                            borderRadius: '4px',
                                                            width: '50%'
                                                        }}></div>
                                                    </div>
                                                    {/* Salary skeleton */}
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        margin: 0
                                                    }}>
                                                        <div style={{
                                                            width: '14px',
                                                            height: '14px',
                                                            background: '#e0e0e0',
                                                            borderRadius: '2px'
                                                        }}></div>
                                                        <div style={{
                                                            height: '14px',
                                                            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                                                            backgroundSize: '200% 100%',
                                                            animation: 'shimmer 1.5s infinite',
                                                            borderRadius: '4px',
                                                            width: '40%'
                                                        }}></div>
                                                    </div>
                                                </>
                                            ) : (
                                                // Actual content when loaded, single column with aligned icons
                                                <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '0.5rem',
                                                    maxWidth: '680px'
                                                }}>
                                                    {/* Email */}
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                                        <span style={{width: '18px', display: 'inline-flex', justifyContent: 'center'}}>
                                                            <i className="fas fa-envelope" style={{color: '#94a3b8'}}></i>
                                                        </span>
                                                        <span style={{
                                                            color: profileData?.email ? '#64748b' : '#94a3b8',
                                                            fontSize: '0.875rem',
                                                            fontStyle: profileData?.email ? 'normal' : 'italic',
                                                            userSelect: 'none',
                                                            WebkitUserSelect: 'none',
                                                            MozUserSelect: 'none',
                                                            msUserSelect: 'none'
                                                        }}>
                                                            {profileData?.email || 'Email not provided'}
                                                        </span>
                                                    </div>

                                                    {/* Phone */}
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                                        <span style={{width: '18px', display: 'inline-flex', justifyContent: 'center'}}>
                                                            <i className="fas fa-phone" style={{color: '#94a3b8'}}></i>
                                                        </span>
                                                        <span style={{ 
                                                            color: '#64748b', 
                                                            fontSize: '0.875rem',
                                                            userSelect: 'none',
                                                            WebkitUserSelect: 'none',
                                                            MozUserSelect: 'none',
                                                            msUserSelect: 'none'
                                                        }}>{userMobile}</span>
                                                    </div>

                                                    {/* Location */}
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                                        <span style={{width: '18px', display: 'inline-flex', justifyContent: 'center'}}>
                                                            <i className="fas fa-map-marker-alt" style={{color: '#94a3b8'}}></i>
                                                        </span>
                                                        <span style={{ 
                                                            color: '#64748b', 
                                                            fontSize: '0.875rem',
                                                            userSelect: 'none',
                                                            WebkitUserSelect: 'none',
                                                            MozUserSelect: 'none',
                                                            msUserSelect: 'none'
                                                        }}>
                                                            {profileData?.cityName && profileData?.pincode 
                                                                ? `${profileData.cityName} - ${profileData.pincode}`
                                                                : profileData?.cityName 
                                                                    ? profileData.cityName
                                                                    : profileData?.pincode 
                                                                        ? `Pincode: ${profileData.pincode}`
                                                                        : 'Location not provided'}
                                                        </span>
                                                    </div>

                                                    {/* Salary */}
                                                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                                        <span style={{width: '18px', display: 'inline-flex', justifyContent: 'center'}}>
                                                            <i className="fas fa-indian-rupee-sign" style={{color: '#94a3b8', fontSize: '1rem', width: '18px', minWidth: '18px', textAlign: 'center', display: 'inline-block'}}></i>
                                                        </span>
                                                        <span style={{ 
                                                            color: '#64748b', 
                                                            fontSize: '0.875rem',
                                                            userSelect: 'none',
                                                            WebkitUserSelect: 'none',
                                                            MozUserSelect: 'none',
                                                            msUserSelect: 'none'
                                                        }}>
                                                            {(() => {
                                                                const raw = profileData?.expectedSalary ?? profileData?.salaryMax;
                                                                if (raw && raw !== 'null') {
                                                                    const cleaned = String(raw).replace(/[^0-9.]/g, '');
                                                                    const num = Number(cleaned);
                                                                    if (Number.isFinite(num) && num > 0) {
                                                                        const hasDecimal = cleaned.includes('.') && !cleaned.endsWith('.');
                                                                        const formatted = num.toLocaleString('en-IN', {
                                                                            minimumFractionDigits: hasDecimal ? 2 : 0,
                                                                            maximumFractionDigits: hasDecimal ? 2 : 0
                                                                        });
                                                                        return `${formatted} per month`;
                                                                    }
                                                                }
                                                                return 'Expected salary not provided';
                                                            })()}
                                                        </span>
                                                    </div>

                                                    {/* Profile data missing notice */}
                                                    {!profileData && (
                                                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                                            <i className="fas fa-exclamation-triangle" style={{color: '#ef4444'}}></i>
                                                            <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>Profile data not available</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Resume Section */}
                        <div id="resume" style={{
                            borderBottom: '1px solid #e5e7eb',
                            paddingBottom: '24px',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '1.5rem'
                            }}>
                                <h3 style={{
                                    margin: '0',
                                    color: '#1f2937',
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem'
                                }}>
                                    <i className="fas fa-file-pdf" style={{color: '#2563eb', fontSize: '1.25rem', width: '20px', textAlign: 'center'}}></i>
                                    Resume
                                </h3>
                            </div>
                            
                            <div style={{
                                background: 'white',
                                borderRadius: '12px',
                                padding: '1.5rem',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                border: '1px solid #e5e7eb'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '1rem'
                                }}>
                                    <span style={{
                                        margin: 0,
                                        color: '#6b7280',
                                        fontSize: '0.95rem',
                                        fontWeight: 500
                                    }}>Upload and manage your resume</span>
                                </div>
                                
                                {/* Current Resume Display - Show if filePath exists */}
                                {resumeFileName && (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '1rem',
                                        background: '#f0f9ff',
                                        border: '1px solid #0ea5e9',
                                        borderRadius: '8px',
                                        marginBottom: '1rem'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem'
                                        }}>
                                            <div>
                                                <h4 style={{
                                                    margin: 0,
                                                    color: '#1f2937',
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600
                                                }}>
                                                    {resumeFileName}
                                                </h4>
                                                <p style={{
                                                    margin: 0,
                                                    color: '#6b7280',
                                                    fontSize: '0.75rem'
                                                }}>Current resume</p>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={!resumeDownloading ? handleDownloadResume : undefined}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: resumeDownloading ? '#9ca3af' : '#2563eb',
                                                cursor: resumeDownloading ? 'not-allowed' : 'pointer',
                                                padding: '0.5rem 0.75rem',
                                                transition: 'color 0.2s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }} 
                                            title={resumeDownloading ? 'Downloading...' : 'Download Resume'}
                                            disabled={resumeDownloading}
                                        >
                                            {resumeDownloading ? (
                                                <>
                                                    <i className="fas fa-spinner fa-spin" style={{fontSize: '1rem'}}></i>
                                                    <span style={{fontSize: '0.875rem'}}>Downloading...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-download" style={{fontSize: '1.125rem'}}></i>
                                                    <span style={{fontSize: '0.875rem'}}>Download</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                                
                                {/* Selected Resume Preview (before upload) */}
                                {selectedResumeFile && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        padding: '1rem',
                                        background: '#fef3c7',
                                        border: '1px solid #f59e0b',
                                        borderRadius: '8px',
                                        marginBottom: '1rem'
                                    }}>
                                        <div style={{
                                            width: '80px',
                                            height: '100px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '6px',
                                            overflow: 'hidden',
                                            background: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            {selectedResumePreviewUrl ? (
                                                <embed src={selectedResumePreviewUrl} type="application/pdf" style={{ width: '100%', height: '100%' }} />
                                            ) : (
                                                <i className="fas fa-file-word" style={{ color: '#2563eb', fontSize: '2rem' }}></i>
                                            )}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <div>
                                                    <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#111827' }}>{selectedResumeFile.name}</h4>
                                                    <p style={{ margin: 0, color: '#6b7280', fontSize: '0.8rem' }}>New file selected (not yet saved)</p>
                                                </div>
                                                {resumeUploading ? (
                                                    <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                                                        <i className="fas fa-spinner fa-spin"></i> Uploading...
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Upload Area - Always visible */}
                                <div style={{
                                    border: '2px dashed #d1d5db',
                                    borderRadius: '8px',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    background: '#f9fafb',
                                    transition: 'all 0.2s'
                                }}>
                                    <i className="fas fa-cloud-upload-alt" style={{
                                        fontSize: '3rem',
                                        color: '#9ca3af',
                                        marginBottom: '1rem'
                                    }}></i>
                                    <h4 style={{
                                        margin: '0 0 0.5rem 0',
                                        color: '#1f2937',
                                        fontSize: '1.125rem'
                                    }}>
                                        {resumeFileName ? 'Upload New Resume' : 'Upload Your Resume'}
                                    </h4>
                                    <p style={{
                                        color: '#6b7280',
                                        marginBottom: '1rem'
                                    }}>Drag and drop your resume file here or click to browse</p>
                                    <input type="file" id="resume-file" accept=".pdf,.doc,.docx" style={{display: 'none'}} onChange={handleResumeUpload} disabled={resumeUploading} />
                                    <button onClick={() => !resumeUploading && document.getElementById('resume-file').click()} style={{
                                        background: resumeUploading ? '#9ca3af' : '#2563eb',
                                        color: 'white',
                                        border: 'none',
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '8px',
                                        cursor: resumeUploading ? 'not-allowed' : 'pointer',
                                        fontWeight: 600,
                                        opacity: resumeUploading ? 0.7 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.5rem',
                                        margin: '0 auto'
                                    }} disabled={resumeUploading}>
                                        {resumeUploading ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin"></i>
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-upload"></i>
                                                {resumeFileName ? 'Choose New File' : 'Choose File'}
                                            </>
                                        )}
                                    </button>
                                    <p style={{
                                        color: '#9ca3af',
                                        fontSize: '0.875rem',
                                        marginTop: '0.5rem'
                                    }}>Supported formats: PDF, DOC, DOCX (Max 5MB)</p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Skills Section */}
                        <div id="skills" style={{
                            borderBottom: '1px solid #e5e7eb',
                            paddingBottom: '24px',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '1.5rem'
                            }}>
                                <h3 style={{
                                    margin: '0',
                                    color: '#1f2937',
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem'
                                }}>
                                    <i className="fas fa-lightbulb" style={{color: '#2563eb', fontSize: '1.25rem', width: '20px', textAlign: 'center'}}></i>
                                    Skills
                                </h3>
                                <button onClick={openSkillsModal} style={{
                                    background: '#f8fafc',
                                    color: '#2563eb',
                                    border: '2px solid #e0e7ff',
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <i className="fas fa-plus"></i> Add Skill
                                </button>
                            </div>
                            
                            <div style={{
                                background: 'white',
                                borderRadius: '12px',
                                padding: '1.5rem',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                border: '1px solid #e5e7eb'
                            }}>
                                {skillsLoading ? (
                                <div style={{
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    border: '2px dashed #e5e7eb',
                                    color: '#2563eb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    <span style={{ fontWeight: 600 }}>Loading skills…</span>
                                </div>
                            ) : userSkills && userSkills.length > 0 ? (
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '0.75rem',
                                    alignItems: 'flex-start'
                                }}>
                                    {userSkills.map((skill, index) => (
                                        <div key={index} style={{
                                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                            color: '#374151',
                                            padding: '0.5rem 1rem',
                                            borderRadius: '20px',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            border: '1px solid #e5e7eb',
                                            display: 'inline-block',
                                            transition: 'all 0.2s ease',
                                            cursor: 'default',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.transform = 'translateY(-1px)';
                                            e.target.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.15)';
                                            e.target.style.borderColor = '#c7d2fe';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                                            e.target.style.borderColor = '#e5e7eb';
                                        }}
                                        >
                                            {skill}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    border: '2px dashed #e5e7eb'
                                }}>
                                    <i className="fas fa-lightbulb" style={{
                                        fontSize: '2rem',
                                        color: '#9ca3af',
                                        marginBottom: '1rem'
                                    }}></i>
                                    <p style={{
                                        margin: '0 0 1rem 0',
                                        color: '#6b7280',
                                        fontSize: '1rem'
                                    }}>
                                        No skills added yet
                                    </p>
                                    <p style={{
                                        margin: 0,
                                        color: '#9ca3af',
                                        fontSize: '0.875rem'
                                    }}>
                                        Click "Add Skill" to showcase your expertise
                                    </p>
                                </div>
                            )}
                            </div>
                        </div>

                        {/* KYC Verification Section - always visible */}
                        <div id="kyc">
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '1.5rem'
                            }}>
                                <h3 style={{
                                    margin: '0',
                                    color: '#1f2937',
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem'
                                }}>
                                    <i className="fas fa-shield-alt" style={{color: '#2563eb', fontSize: '1.25rem', width: '20px', textAlign: 'center'}}></i>
                                    KYC Verification
                                </h3>
                            </div>
                            
                            <KycVerificationWidget 
                                userProfile={{
                                    ...profileData,
                                    ...kycStatus
                                }}
                                onVerificationStatusChanged={handleKycVerificationStatusChanged}
                            />
                        </div>
                    </div>
                </main>
                
                {/* Right Sidebar - Profile Completion */}
                {windowWidth > 768 && (
                    <aside className="profile-right-sidebar">
                        {renderProfileCompletionCard()}
                    </aside>
                )}
            </div>
            
            {/* Edit Profile Modal */}
            <EditProfileModal 
                isOpen={isEditModalOpen} 
                onClose={closeEditModal}
                profileData={profileData}
                onProfileUpdate={loadCandidateData}
                kycStatus={kycStatus}
            />
            
            {/* Add Skill Modal */}
            <AddSkillModal 
                isOpen={isSkillModalOpen}
                onClose={closeSkillsModal}
                onSaveSkills={handleSaveSkills}
                currentSkills={userSkills}
                allActiveSkills={allActiveSkills}
                mobileNumber={(profileData && profileData.mobile) || getMobileNumber()}
            />
        </div>
    );
};

export default Profile;
